import React, { useRef } from 'react';
import { useNavigate } from "react-router-dom";

import SectionWrapper from '../UI/SectionWrapper';
import questions from "../../../Assets/products/questions.jpg";
import dataSync from "../../../Assets/products/data-synchronisation.jpg";
import migration from "../../../Assets/products/migration.jpg";
import dashboard from "../../../Assets/products/dashboard.jpg";

import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Mousewheel, Navigation, Keyboard, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { COLORS } from "../../../Constants/colors";

const PainList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, 1fr);
  gap: 1.25rem;
  list-style: none;
  padding: 0;
  margin: 0 auto;
  width: 100%;
`;

const PainItem = styled.li`
  background-color: ${COLORS.background};
  color: ${COLORS.textPrimary};
  padding: 1rem 1.5rem;
  border-left: 4px solid ${COLORS.primary};
  border-radius: 10px;
  font-size: 1.2rem;
  text-align: left;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${COLORS.cardHoverBackground};
    color: ${COLORS.primary};
    transform: translateX(5px);
  }
`;

const CarouselSection = styled.section`
  height: 680px;
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: 1028px) {
    height: auto;
  }
`;

// 2. Carousel Product Section Enhancements
const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background-color: ${COLORS.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${COLORS.shadowColor};
  overflow: hidden;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  background: ${COLORS.imageBackground};
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;

  @media (max-width: 768px) {
    min-height: 200px;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: fit;
  padding: 1rem;
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;
  }
`;

const Heading = styled.h3`
  font-size: 0.875rem;
  color: ${COLORS.primary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: ${COLORS.textPrimary};
`;

const Body = styled.p`
  font-size: 1.5rem;
  color: ${COLORS.mutedText};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ReadMore = styled.a`
  align-self: flex-start;
  padding: 0.5rem 1rem;
  background-color: ${COLORS.primary};
  color: ${COLORS.textSecondary};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${COLORS.secondary};
    color: ${COLORS.textPrimary};
  }

  @media (max-width: 768px) {
    align-self: center;
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ExpandButton = styled.button`
  align-self: flex-start;
  padding: 10px 16px;
  border: 2px solid ${COLORS.primary};
  color: ${COLORS.primary};
  background: transparent;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease-in-out;

  &:hover {
    background: ${COLORS.primary};
    color: ${COLORS.buttonText};
  }

  @media (max-width: 768px) {
    align-self: center;
  }
`;

const PainPointsContent = () => (
  <Card>
    <ImageContainer>
      <Image src={questions} alt="SmartSync" />
    </ImageContainer>
    <Content>
      <Heading>Are You Struggling With</Heading>
      <PainList>
        <PainItem>Business with distributed data across locations</PainItem>
        <PainItem>Difficult to collate your business data for analysis</PainItem>
        <PainItem>New business potential if you get your data online</PainItem>
        <PainItem>Lacking business intelligence, real-time dashboards and reports</PainItem>
        <PainItem>Planning to migrate to Newgen technologies</PainItem>
        <PainItem>Business data in DBF, CSV, Other sources</PainItem>
      </PainList>
    </Content>
  </Card>
);

const ProductCarousel = ({ slides }) => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  return (
    <CarouselWrapper
      // onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
      // onMouseLeave={() => swiperRef.current?.autoplay?.start()}
    >
      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        spaceBetween={30}
        pagination={{ clickable: true }}
        loop={true}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        navigation={true}
        modules={[Autoplay, Navigation, Pagination, Mousewheel, Keyboard]}
        className="productSwiper"
        style={{ padding: "2rem 0", width: "100%" }}
      >
        {slides.map((slide) => (
          <SwiperSlide
            key={slide.id}
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100%",
            }}
          >
            {slide.type === "bullets" ? (
      <PainPointsContent />
    ) : (
      <Card>
        <ImageContainer>
          <Image src={slide.image} alt={slide.title}/>
        </ImageContainer>
        <Content>
          <Heading>{slide.heading}</Heading>
          <Title>{slide.title}</Title>
          <Body>{slide.body}</Body>
          {/* <ReadMore href={slide.link} target="_blank" rel="noopener noreferrer">
            Read more →
          </ReadMore> */}
          <ExpandButton onClick={() => navigate(slide.link)}>
            Read More →
          </ExpandButton>
        </Content>
      </Card>
    )}
  </SwiperSlide>
        ))}
      </Swiper>
    </CarouselWrapper>
  );
};
// Data for carousel slides
const slidesData = [
  {
    id: "0",
    type: "bullets",
  },
  {
    id: "1",
    heading: "SmartSync",
    title: "SmartSync as a Realtime Data Synchronization tool",
    body: "SmartSync is a premier solution for managing distributed and offline data. With our state-of-the-art technology, you can seamlessly synchronize the distributed and offline data to make it available near to real-time at the centralized database.",
    image: dataSync,
    link: "/products",
  },
  {
    id: "2",
    heading: "SmartSync",
    title: "SmartSync as a Business data Collation and Reporting Tool",
    body: "Experience the power of seamless data collation with SmartSync. With just a few clicks, you can collate business data from multiple sources and generate customised reports and interactive dashboards for in-depth analysis.",
    image: dashboard,
    link: "/products",
  },
  {
    id: "3",
    heading: "SmartSync",
    title: "SmartSync as a Data Migration Tool",
    body: "Introducing SmartSync, the ultimate migration tool for seamless data transition. Whether you are moving to a new system or consolidating data from multiple sources, SmartSync ensures data integrity and accuracy throughout the migration journey.",
    image: migration,
    link: "/products",
  },
];

// Main Products Component
function Products() {
    return (
        <SectionWrapper heading="PRODUCTS">
            <CarouselSection>
                <ProductCarousel slides={slidesData} />
            </CarouselSection>
        </SectionWrapper>
    );
}

export default Products;